/**
 * What suppliers have actually quoted for an item — read from the quotations themselves.
 *
 * ---- Why not `items.get_purchase_history` ----
 *
 * Because it does not work. It answers `[]` for every item regardless of arguments (recorded in
 * `useSuppliers.ts`), so the quote-history view built on it shows "nobody has quoted this" for
 * items that have been quoted many times.
 *
 * The data is not missing, only that method's view of it: every past quote is a
 * `Supplier Quotation Item` row. Reading those directly gives the real answer today, with no
 * backend change.
 *
 * ---- Two requests, and why the child read needs `parent` ----
 *
 * 1. `Supplier Quotation Item` filtered by `item_code` — the rates. Frappe refuses a child-table
 *    list read without `parent` (`PermissionError: Insufficient Permission`) and allows it with,
 *    because permission is then checked against the parent doctype. Verified live for
 *    `Enquiry Item` on 2026-08-09 (`frappe.ts`'s `GatewayQuery.parent`); this is the same
 *    mechanism on a different child.
 * 2. `Supplier Quotation` for those parents — the child row knows the rate but not WHO quoted it;
 *    supplier and currency live on the parent document.
 *
 * Fired only when the history modal opens, so two requests on a deliberate click is the whole
 * cost, and nothing is fetched for a table of sixty rows nobody expanded.
 *
 * ---- Fails soft, on purpose ----
 *
 * Every failure resolves to an EMPTY LIST rather than throwing: this replaces a view that was
 * already empty, so the worst case is exactly the status quo — never a broken screen. If the
 * child read turns out to be refused on this doctype, the modal keeps saying "nobody has quoted
 * this item", which is what it says today.
 */

import { useQuery } from '@tanstack/react-query'
import { getResource } from './frappe'
import { callGet } from './bosun'
import { num, str } from './mappers/coerce'
import { getBaseCurrency } from './baseCurrency'
import { money } from '../money'
import type { PurchaseHistoryEntry } from '../types'

const SQ_ITEM_DOCTYPE = 'Supplier Quotation Item'
const SQ_DOCTYPE = 'Supplier Quotation'

/** Enough to see a trend; a purchaser reading more than this wants the comparison screen.
 *  Used by the generic-read fallback below, not by the hook — see its own note. */
export const MAX_ROWS = 50

interface WireQuoteRow {
  parent?: unknown
  rate?: unknown
  uom?: unknown
  creation?: unknown
  /**
   * What the supplier said ABOUT this offer — "Delivery 3 days · The above costing is based
   * inches" (measured live 2026-08-31).
   *
   * A price without it is half the offer: a rate three days out, and a rate quoted against the
   * wrong unit, are not comparable, and nothing in the number itself says so. `remark` is the one
   * field here that both reads and carries data — `supplier_description` is null throughout, and
   * `brand`/`lead_time_days` are refused by the list query ("Field not permitted in query").
   */
  remark?: unknown
}

interface WireQuoteParent {
  name?: unknown
  supplier?: unknown
  currency?: unknown
  transaction_date?: unknown
  creation?: unknown
}

/**
 * Child rows + their parents → the shape the history view already renders.
 *
 * A row whose parent did not come back is DROPPED, not shown with a blank supplier: "someone
 * quoted 12.50" is not information a purchaser can use, and an empty supplier column would group
 * every such row together as if one supplier had quoted them all.
 */
export function toQuoteHistory(
  rows: WireQuoteRow[],
  parents: WireQuoteParent[],
  itemCode: string,
): PurchaseHistoryEntry[] {
  const byName = new Map(parents.map((p) => [str(p.name), p]))
  const out: PurchaseHistoryEntry[] = []

  for (const row of rows) {
    const parentName = str(row.parent)
    const parent = byName.get(parentName)
    const supplier = str(parent?.supplier)
    const rate = num(row.rate)
    // A zero rate is a declined reply, not a price — the same rule `quoteBenchmark.ts` applies.
    if (!parent || supplier === '' || rate <= 0) continue
    out.push({
      item: itemCode,
      supplier,
      price: money(rate, str(parent.currency) || 'AED'),
      uom: str(row.uom),
      ref: parentName,
      type: 'SQ',
      // The quotation's own date where it has one; the row's creation otherwise.
      date: str(parent.transaction_date) || str(parent.creation) || str(row.creation),
      customer: '',
      remark: str(row.remark).trim(),
    })
  }
  return out
}

/** One row as `items.get_supplier_rates_history` sends it. */
export interface WireRatesHistory {
  item?: unknown
  supplier?: unknown
  price?: unknown
  type?: unknown
  remark?: unknown
  date?: unknown
  ref?: unknown
  uom?: unknown
  currency?: unknown
}

/**
 * `DD-MM-YYYY` -> `YYYY-MM-DD`, the format every other date on this transport uses.
 *
 * This method is the ONLY one that sends day-first, and it matters more than it looks:
 * `usePurchaseHistory` narrows to a window with `entry.date >= cutoff` compared as STRINGS. Left
 * as sent, `04-09-2026` sorts before a `2026-07-10` cutoff and the row is dropped however recent
 * it is — silently, and in a way that looks like the item simply has no history.
 *
 * An unrecognised date becomes `''`, which that filter treats as "we do not know when" and KEEPS.
 * Guessing a date would put a wrong one on screen beside a real price.
 */
export function toIsoDate(value: unknown): string {
  const raw = str(value).trim()
  if (raw === '') return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10)
  const dayFirst = /^(\d{2})-(\d{2})-(\d{4})$/.exec(raw)
  return dayFirst ? `${dayFirst[3]}-${dayFirst[2]}-${dayFirst[1]}` : ''
}

/**
 * One history row, domain-shaped.
 *
 * The CURRENCY comes off the row and is never defaulted. Measured 2026-09-10: `0005010001`
 * answers USD and `4702110001TEST` answers AED and USD on the same item, so a hardcoded `'AED'`
 * would label a dollar price as dirhams — the defect that once ranked six USD offers at 1/3.65 of
 * their real cost.
 */
export function toRatesHistoryEntry(wire: WireRatesHistory): PurchaseHistoryEntry {
  return {
    item: str(wire.item),
    supplier: str(wire.supplier),
    price: money(num(wire.price), str(wire.currency) || getBaseCurrency()),
    uom: str(wire.uom),
    ref: str(wire.ref),
    type: str(wire.type) === 'PO' ? 'PO' : 'SQ',
    date: toIsoDate(wire.date),
    // Not on this wire. Left empty rather than guessed — see `PurchaseHistoryEntry`.
    customer: '',
    remark: str(wire.remark).trim(),
  }
}

export function useItemQuoteHistory(itemCode: string | null | undefined) {
  return useQuery({
    queryKey: ['item-quote-history', itemCode ?? ''] as const,
    queryFn: async (): Promise<PurchaseHistoryEntry[]> => {
      const code = itemCode as string
      /*
        ONE call, on a purpose-built method (`items.get_supplier_rates_history`, live 2026-09-10).

        This used to be two generic reads: `Supplier Quotation Item` filtered on `item_code`, then
        the parent `Supplier Quotation`s to resolve supplier, currency and date. That worked, but it
        read a CHILD TABLE through `/api/resource`, which needs the `parent` parameter and is
        permission-checked as its parent — a direct read answers `403 PermissionError` for a user
        without Supplier Quotation rights, and the history silently came back empty for them.

        The new method also carries `type` and `ref`, which the two-read version had to infer.

        The generic path and its mapper (`toQuoteHistory`) are kept below, unused by this hook, as
        the fallback the day this method changes shape — deleting them would mean rebuilding the
        join from scratch.
      */
      try {
        const wire = await callGet<WireRatesHistory[]>('items', 'get_supplier_rates_history', {
          item_code: code,
        })
        return Array.isArray(wire) ? wire.map(toRatesHistoryEntry) : []
      } catch {
        // See the module header: an empty list is the status quo, a thrown error is a broken modal.
        return []
      }
    },
    enabled: !!itemCode,
    staleTime: 5 * 60_000,
  })
}

/**
 * The same read for MANY items at once — one request, not one per row.
 *
 * The supplier-quotations screen shows every quoted line on an enquiry, and asking per line would
 * be an N+1 across a table that routinely runs to dozens of rows. Frappe's `in` filter takes the
 * whole set, exactly as `fetchItemPrices` does for prices.
 */
export function useItemQuoteHistories(itemCodes: string[]) {
  const codes = [...new Set(itemCodes.filter((c) => c !== ''))].sort()

  return useQuery({
    queryKey: ['item-quote-history', 'batch', codes.join(',')] as const,
    queryFn: async (): Promise<Map<string, PurchaseHistoryEntry[]>> => {
      const out = new Map<string, PurchaseHistoryEntry[]>()
      try {
        const rows = await getResource<WireQuoteRow & { item_code?: unknown }>(SQ_ITEM_DOCTYPE, {
          fields: ['parent', 'item_code', 'rate', 'uom', 'creation', 'remark'],
          filters: [['item_code', 'in', codes]],
          parent: SQ_DOCTYPE,
          // Generous: this is every quote for every item on one enquiry, not a catalogue scan.
          limit: Math.max(codes.length * 20, 100),
          orderBy: 'creation desc',
        })
        const names = [...new Set(rows.data.map((r) => str(r.parent)).filter((n) => n !== ''))]
        if (names.length === 0) return out

        const parents = await getResource<WireQuoteParent>(SQ_DOCTYPE, {
          fields: ['name', 'supplier', 'currency', 'transaction_date', 'creation'],
          filters: [['name', 'in', names]],
          limit: names.length,
        })
        for (const code of codes) {
          const forItem = rows.data.filter((r) => str(r.item_code) === code)
          out.set(code, toQuoteHistory(forItem, parents.data, code))
        }
        return out
      } catch (error) {
        /*
          THROWS, unlike the single-item read, and deliberately.

          Both used to swallow the failure and return nothing, which made "this item has never
          been quoted" and "we could not read the quotations" render identically — as a blank.
          Asked "where is the history?", nobody could tell which had happened.

          The caller still renders `data ?? empty`, so a failure degrades exactly as before; it
          just also gets `isError`, and can say so. See `InboundRepliesTable`'s note.
        */
        throw error instanceof Error ? error : new Error(String(error))
      }
    },
    enabled: codes.length > 0,
    staleTime: 5 * 60_000,
  })
}

/**
 * What this supplier last quoted for this item, BEFORE the quotation being looked at.
 *
 * `excludeRef` is the current quotation's own name — without it the "previous" price would be the
 * one on screen, and every line would report a 0% change against itself.
 */
export function previousQuoteBy(
  history: readonly PurchaseHistoryEntry[] | undefined,
  supplier: string,
  excludeRef: string,
): PurchaseHistoryEntry | undefined {
  return (history ?? [])
    .filter((e) => e.supplier === supplier && e.ref !== excludeRef)
    .sort((a, b) => (a.date < b.date ? 1 : -1))[0]
}
